import { apiConfig, isTestingMode } from './config';
import { mockDataService } from './services/mockData';
import { mockCampaignsService } from './services/mockCampaigns';
import { mockConsumerLimitsService } from './services/mockConsumerLimits';
import { logger } from './utils/logger';
import { storage } from './utils/storage';
import { handleApiError } from './middleware/apiMiddleware';

// Avoid circular import by defining a function to get auth service
let getAuthService = null;
export const setAuthService = (authService) => {
  getAuthService = () => authService;
};

// Request queue for token refresh to prevent concurrent refresh attempts
let isRefreshingToken = false;
let refreshPromise = null;
let requestQueue = [];

/**
 * Check if token should be refreshed proactively
 * Refresh if token expires in less than 5 minutes
 * @returns {boolean} Whether token should be refreshed
 */
const shouldRefreshToken = () => {
  const expiresAt = storage.getItem('token_expires_at');
  if (!expiresAt) {
    // If no expiration time stored, check if we have a refresh token
    // This handles cases where expiration wasn't stored but we have valid cookies
    return false;
  }
  
  try {
    const expiryTime = new Date(expiresAt).getTime();
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    // Refresh if token expires in less than 5 minutes
    const shouldRefresh = (expiryTime - now) < fiveMinutes;
    
    if (shouldRefresh) {
      logger.debug(`🔄 Token expires in ${Math.round((expiryTime - now) / 1000)}s, should refresh`);
    }
    
    return shouldRefresh;
  } catch (error) {
    logger.error('Error checking token expiration:', error);
    return false;
  }
};

/**
 * Queue requests during token refresh to prevent multiple concurrent refresh attempts
 * @param {Function} requestFunction - Function that makes the API request
 * @returns {Promise} Promise that resolves when the request is processed
 */
const queueRequest = (requestFunction) => {
  return new Promise((resolve, reject) => {
    requestQueue.push({ resolve, reject, requestFunction });
  });
};

/**
 * Process all queued requests after token refresh
 */
const processQueue = (error = null, newToken = null) => {
  const queue = requestQueue.slice(); // Copy the queue
  requestQueue = []; // Clear the queue

  queue.forEach(({ resolve, reject, requestFunction }) => {
    if (error) {
      reject(error);
    } else {
      requestFunction(newToken).then(resolve).catch(reject);
    }
  });
};

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(message, status, response) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.response = response;
  }
}

/**
 * Check if an error is an expected authentication failure
 */
const isAuthError = (status) => {
  return status === 401 || status === 403;
};

/**
 * Check if logging should be suppressed for this request
 */
const shouldSuppressLogging = (options) => {
  return options.suppressAuthErrorLogging === true;
};

/**
 * Base fetch wrapper with common configuration
 */
const baseFetch = async (endpoint, options = {}) => {
  const url = `${apiConfig.baseUrl}${endpoint}`;

  const config = {
    timeout: apiConfig.timeout,
    credentials: 'include',  // P1: Always send/receive cookies
    headers: {
      ...apiConfig.headers,
      ...options.headers,
    },
    ...options,
  };

  // IMPORTANT: Remove Content-Type header for FormData requests
  // The browser will set the correct Content-Type with boundary automatically
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  // P2 SECURITY: Add CSRF token for state-changing requests
  const isStateChanging = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method);
  if (isStateChanging && getAuthService) {
    const authService = getAuthService();
    const csrfToken = authService.getCSRFToken();
    
    // Endpoints that don't need CSRF token (must match backend EXEMPT_ROUTES)
    const csrfExemptEndpoints = [
      '/api/v1/admin/auth/login',
      '/api/v1/admin/auth/login-otp',
      '/api/v1/admin/auth/verify-otp',
      '/api/v1/admin/auth/forgot-password',
      '/api/v1/admin/auth/reset-password',
      '/api/v1/admin/auth/refresh',
      '/api/v1/admin/auth/logout',
      '/api/v1/admin/campaigns',
      '/api/v1/admin/company-wallet',
      '/docs',
      '/redoc',
      '/openapi.json',
      '/health',
      '/metrics',
    ];
    
    const isCsrfExempt = csrfExemptEndpoints.some(exemptPath => endpoint.startsWith(exemptPath));
    
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
      logger.debug('🔐 Added CSRF token to request headers:', csrfToken.substring(0, 20) + '...');
    } else if (!isCsrfExempt) {
      // Only throw error if it's not a CSRF-exempt request
      logger.error('⚠️ CSRF token not available for state-changing request.');
      logger.error('⚠️ Please logout and login again to get a fresh CSRF token.');
      
      // Throw a user-friendly error
      throw new ApiError(
        'Security token missing. Please logout and login again to continue.',
        403,
        { 
          code: 'CSRF_TOKEN_MISSING',
          message: 'CSRF token not found in memory. This can happen after a security update. Please logout and login again.'
        }
      );
    } else {
      logger.debug('🔐 CSRF-exempt endpoint - CSRF token not required');
    }
  }

  // Only log detailed request info if not suppressing auth error logging
  if (!shouldSuppressLogging(options)) {
    // Handle FormData logging differently
    let bodyLog = undefined;
    if (config.body) {
      if (config.body instanceof FormData) {
        bodyLog = '[FormData]';
      } else if (typeof config.body === 'string') {
        try {
          bodyLog = JSON.parse(config.body);
        } catch {
          bodyLog = config.body;
        }
      } else {
        bodyLog = config.body;
      }
    }

    logger.apiRequest(`${config.method || 'GET'} ${url}`, {
      method: config.method || 'GET',
      url,
      headers: config.headers,
      body: bodyLog
    });
  }

  // Add timeout functionality
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeout);

  try {
    const response = await fetch(url, {
      ...config,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Only log response details if not suppressing auth error logging or if it's successful
    if (!shouldSuppressLogging(options) || response.ok) {
      logger.apiResponse(`${response.status} ${url}`, {
        url,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
    }

    // Handle non-200 responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Only log error details if it's not an expected auth error or if logging is not suppressed
      if (!isAuthError(response.status) || !shouldSuppressLogging(options)) {
        logger.apiError(`${response.status} ${url}`, { url, status: response.status, errorData });
      } else {
        // For auth errors with suppressed logging, just log a minimal message
        logger.auth(`Authentication required for: ${url}`);
      }

      // Create API error with proper structure
      const apiError = new ApiError(
        errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData
      );

      // Use middleware to handle error (unless suppressed)
      // Services can suppress middleware toasts if they want to show custom messages
      // By default, middleware toasts are SUPPRESSED to let services handle messaging
      if (!options.suppressMiddleware && options.enableMiddlewareToast) {
        handleApiError(apiError, {
          suppressToast: options.suppressErrorToast || shouldSuppressLogging(options),
        });
      }

      throw apiError;
    }

    // Return response for further processing
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    // Don't log auth errors as actual failures if logging is suppressed
    if (error instanceof ApiError && isAuthError(error.status) && shouldSuppressLogging(options)) {
      // Just rethrow without logging for expected auth failures
      throw error;
    }

    logger.apiError(`Request Failed: ${url}`, { url, error: error.message, type: error.name });

    let apiError;
    if (error.name === 'AbortError') {
      apiError = new ApiError('Request timeout', 408);
    } else if (error instanceof ApiError) {
      apiError = error;
    } else {
      // Network or other errors - provide user-friendly message
      let userMessage = 'Unable to connect to server';
      if (error.message === 'Failed to fetch') {
        userMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      } else if (error.message?.includes('NetworkError')) {
        userMessage = 'Network error. Please check your internet connection.';
      } else if (error.message) {
        userMessage = error.message;
      }
      apiError = new ApiError(userMessage, 0, { originalError: error });
    }

    // Use middleware to handle error (unless suppressed)
    // By default, middleware toasts are SUPPRESSED to let services handle messaging
    if (!options.suppressMiddleware && options.enableMiddlewareToast && !(error instanceof ApiError)) {
      handleApiError(apiError, {
        suppressToast: options.suppressErrorToast || false,
      });
    }

    throw apiError;
  }
};

/**
 * Helper function to route API calls to mock data service when in testing mode
 */
const routeToMockIfTesting = async (endpoint, method, data = null, params = {}) => {
  const testingMode = isTestingMode();
  logger.info('Testing Mode Check:', {
    testingMode,
    envVar: process.env.NEXT_PUBLIC_TESTING_DATA,
    endpoint
  });

  if (!testingMode) {
    return null; // Return null to indicate we should proceed with real API call
  }

  logger.info('Testing Mode: Using mock data for:', { endpoint, method, data, params });

  // Route to appropriate mock service based on endpoint
  try {
    // Consumer limits mocks. First, because `/api/v1/admin/users/{id}/limits`
    // would otherwise reach the generic fallthrough below and come back as
    // "Mock endpoint not implemented" with a 200.
    const consumerLimitsResponse = await mockConsumerLimitsService.handle(endpoint, method, data);
    if (consumerLimitsResponse !== null) {
      return consumerLimitsResponse;
    }

    // Campaigns mocks
    if (endpoint.includes('/api/v1/admin/campaigns')) {
      if (endpoint.includes('/overview')) {
        return await mockCampaignsService.getOverview();
      }

      const participantsMatch = endpoint.match(/\/campaigns\/([^\/]+)\/participants$/);
      if (participantsMatch) {
        return await mockCampaignsService.getParticipants(participantsMatch[1]);
      }

      const participantDetailsMatch = endpoint.match(/\/campaigns\/([^\/]+)\/participants\/([^\/]+)$/);
      if (participantDetailsMatch) {
        return await mockCampaignsService.getParticipantDetails(participantDetailsMatch[1], participantDetailsMatch[2]);
      }

      const campaignIdMatch = endpoint.match(/\/campaigns\/([^\/]+)$/);
      if (campaignIdMatch && !endpoint.includes('overview') && !endpoint.includes('participants')) {
        return await mockCampaignsService.getCampaign(campaignIdMatch[1]);
      }

      if (endpoint === '/api/v1/admin/campaigns') {
        return await mockCampaignsService.getCampaigns(params?.status);
      }
    }

    // Authentication endpoints
    if (endpoint.includes('/api/v1/admin/auth/login-otp')) {
      return await mockDataService.auth.adminLoginOtp(data.email);
    }
    if (endpoint.includes('/api/v1/admin/auth/verify-otp')) {
      return await mockDataService.auth.adminVerifyOtp(data);
    }
    if (endpoint.includes('/api/v1/admin/auth/login')) {
      return await mockDataService.auth.adminLogin(data);
    }
    if (endpoint.includes('/api/v1/admin/auth/change-password')) {
      return await mockDataService.auth.adminChangePassword(data);
    }
    if (endpoint.includes('/api/v1/admin/auth/refresh')) {
      return await mockDataService.auth.refreshAccessToken();
    }

    // Dashboard endpoints
    if (endpoint.includes('/api/v1/admin/dashboard/overview')) {
      const period = params.period || 'monthly';
      const metric = params.metric || null;
      return await mockDataService.dashboard.overview(period, metric);
    }
    if (endpoint.includes('/api/v1/admin/dashboard/users')) {
      // Check for specific user endpoints first
      const userIdMatch = endpoint.match(/\/users\/([^\/]+)/);
      if (userIdMatch) {
        const userId = userIdMatch[1];

        // User transactions breakdown endpoint
        if (endpoint.includes('/transactions/breakdown')) {
          return await mockDataService.dashboard.userTransactionBreakdown(userId, params);
        }

        // User transactions endpoint (legacy)
        if (endpoint.includes('/transactions')) {
          return await mockDataService.dashboard.userTransactions(userId, params);
        }

        // User security data endpoint
        if (endpoint.includes('/security-data')) {
          return await mockDataService.dashboard.userSecurityData(userId);
        }

        // User documents endpoint
        if (endpoint.includes('/documents')) {
          return await mockDataService.dashboard.userDocuments(userId);
        }

        // User details endpoint (base user info)
        if (endpoint.match(/\/users\/[^\/]+$/)) {
          return await mockDataService.dashboard.userDetails(userId);
        }
      }

      // Users list endpoint
      return await mockDataService.dashboard.users(params);
    }
    if (endpoint.includes('/api/v1/admin/dashboard/transactions')) {
      return await mockDataService.dashboard.transactions(params);
    }

    // User-facing transaction endpoints
    if (endpoint.includes('/api/v1/transactions/me/breakdown')) {
      return await mockDataService.dashboard.userTransactionBreakdown(null, params);
    }

    // Standalone Transactions endpoints (not dashboard)
    if (endpoint.includes('/api/v1/admin/transactions')) {
      logger.info('Testing Mode: Admin transactions endpoint called:', endpoint);
      logger.info('Testing Mode: Parameters:', params);

      // Check for specific transaction endpoints first
      const transactionIdMatch = endpoint.match(/\/transactions\/([^\/]+)/);
      if (transactionIdMatch) {
        const transactionId = transactionIdMatch[1];

        // Receipt generation endpoint
        if (endpoint.includes('/receipt')) {
          const format = data?.format || 'pdf';
          return await mockDataService.dashboard.generateReceipt(transactionId, format);
        }

        // Email receipt endpoint
        if (endpoint.includes('/email')) {
          return await mockDataService.dashboard.emailReceipt(transactionId, data.recipient_email, data.message);
        }

        // Individual transaction details endpoint
        if (endpoint.match(/\/transactions\/[^\/]+$/)) {
          return await mockDataService.dashboard.transactionDetails(transactionId);
        }
      }

      // Transaction stats endpoint
      if (endpoint.includes('/overview/stats')) {
        return await mockDataService.dashboard.transactionStats(params.start_date, params.end_date);
      }

      // General transactions list endpoint
      logger.info('Testing Mode: Calling mockDataService.dashboard.transactions with params:', params);
      const result = await mockDataService.dashboard.transactions(params);
      logger.info('Testing Mode: Mock transactions result:', result);
      return result;
    }
    if (endpoint.includes('/api/v1/admin/dashboard/user-distribution')) {
      return await mockDataService.dashboard.userDistribution();
    }
    if (endpoint.includes('/api/v1/admin/dashboard/transaction-heatmap')) {
      const year = params.year || '2025';
      return await mockDataService.dashboard.transactionHeatmap(year);
    }
    if (endpoint.includes('/api/v1/admin/dashboard/recent-activities')) {
      return await mockDataService.dashboard.recentActivities();
    }

    // Compliance endpoints
    if (endpoint.includes('/api/v1/admin/compliance/kyc/statistics')) {
      return await mockDataService.compliance.getKycStatistics();
    }
    if (endpoint.includes('/api/v1/admin/compliance/aml/risk-distribution')) {
      return await mockDataService.compliance.getAmlRiskDistribution();
    }
    if (endpoint.includes('/api/v1/admin/compliance/aml/flagged-users-timeline')) {
      const timeFilter = params.timeFilter || 'Monthly';
      return await mockDataService.compliance.getAmlFlaggedTimeline(timeFilter);
    }
    if (endpoint.includes('/api/v1/admin/compliance/aml/monitoring')) {
      return await mockDataService.compliance.getAmlMonitoringUsers(params);
    }
    if (endpoint.includes('/api/v1/admin/compliance/aml/run-check')) {
      const userId = data?.user_id || params.user_id;
      return await mockDataService.compliance.runAmlCheck(userId);
    }
    if (endpoint.includes('/api/v1/admin/compliance/sars/login-attempts')) {
      return await mockDataService.compliance.getSuspiciousLoginAttempts(params);
    }
    if (endpoint.includes('/api/v1/admin/compliance/sars/transactions')) {
      if (endpoint.match(/\/transactions\/(\d+)$/)) {
        const id = endpoint.match(/\/transactions\/(\d+)$/)[1];
        if (method === 'PATCH') {
          // For resolving transactions - could add specific resolve function later
          return { success: true, message: 'Transaction resolved successfully' };
        }
        return await mockDataService.compliance.getSarTransactionDetails(id);
      }
      return await mockDataService.compliance.getSuspiciousTransactions(params);
    }

    // Analytics endpoints
    if (endpoint.includes('/api/v1/admin/analytics/transaction-volume')) {
      return await mockDataService.analytics.transactionVolume(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/transaction-distribution')) {
      return await mockDataService.analytics.transactionDistribution(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/revenue-breakdown')) {
      return await mockDataService.analytics.revenueBreakdown(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/revenue-trend')) {
      return await mockDataService.analytics.revenueTrend(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/user-engagement')) {
      return await mockDataService.analytics.userEngagement(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/retention-rate')) {
      return await mockDataService.analytics.retentionRate(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/top-users')) {
      return await mockDataService.analytics.topUsers(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/cash-flow')) {
      return await mockDataService.analytics.cashFlow(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/cash-out-comparison')) {
      return await mockDataService.analytics.cashOutComparison(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/net-cash-flow')) {
      return await mockDataService.analytics.netCashFlow(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/bill-payments')) {
      return await mockDataService.analytics.billPayments(params);
    }
    if (endpoint.includes('/api/v1/admin/analytics/bill-distribution')) {
      return await mockDataService.analytics.billDistribution(params);
    }

    // Exchange rates endpoints
    if (endpoint.includes('/api/v1/admin/exchange-rates/historical')) {
      return await mockDataService.exchangeRates.historical(params);
    }
    if (endpoint.includes('/api/v1/admin/exchange-rates/currencies')) {
      return await mockDataService.exchangeRates.currencies();
    }
    if (endpoint.includes('/api/v1/admin/exchange-rates')) {
      return await mockDataService.exchangeRates.rates(params);
    }

    // Admin management endpoints
    if (endpoint.includes('/api/v1/admin/management/statistics')) {
      return await mockDataService.adminManagement.statistics();
    }
    if (endpoint.includes('/api/v1/admin/auth/me')) {
      return await mockDataService.adminManagement.getCurrentAdmin();
    }
    if (endpoint.includes('/api/v1/admin/management/admins')) {
      if (method === 'POST') {
        return await mockDataService.adminManagement.createAdmin(data);
      }
      if (endpoint.match(/\/admins\/([A-Za-z0-9]+)/)) {
        const id = endpoint.match(/\/admins\/([A-Za-z0-9]+)/)[1];
        if (endpoint.includes('/activity')) {
          return await mockDataService.adminManagement.adminActivity(id);
        }
        if (endpoint.includes('/sessions')) {
          return await mockDataService.adminManagement.adminSessions(id);
        }
        return await mockDataService.adminManagement.adminDetails(id);
      }
      return await mockDataService.adminManagement.admins(params);
    }

    // Notifications endpoints (generic - not admin specific)
    if (endpoint.includes('/notifications')) {
      // Check for specific notification endpoints first
      const notificationIdMatch = endpoint.match(/\/notifications\/([^\/]+)/);
      if (notificationIdMatch) {
        const notificationId = notificationIdMatch[1];

        // Analytics endpoint
        if (endpoint.includes('/analytics')) {
          return await mockDataService.notifications.getAnalytics(notificationId);
        }

        // Cancel endpoint
        if (endpoint.includes('/cancel')) {
          return await mockDataService.notifications.cancel(notificationId, data.reason);
        }

        // Resend endpoint
        if (endpoint.includes('/resend')) {
          return await mockDataService.notifications.resend(notificationId);
        }

        // Send endpoint
        if (endpoint.includes('/send')) {
          return await mockDataService.notifications.send(notificationId);
        }

        // Schedule endpoint
        if (endpoint.includes('/schedule')) {
          return await mockDataService.notifications.schedule(notificationId);
        }

        // Individual notification details endpoint
        if (endpoint.match(/\/notifications\/[^\/]+$/)) {
          return await mockDataService.notifications.getById(notificationId);
        }
      }

      // Analytics summary endpoint
      if (endpoint.includes('/analytics/summary')) {
        return await mockDataService.notifications.getAnalyticsSummary();
      }

      // CRUD operations on notifications collection
      if (method === 'POST') {
        return await mockDataService.notifications.create(data);
      }
      if (method === 'PUT' || method === 'PATCH') {
        const id = endpoint.match(/\/notifications\/(\d+)/)?.[1];
        return await mockDataService.notifications.update(id, data);
      }
      if (method === 'DELETE') {
        const id = endpoint.match(/\/notifications\/(\d+)/)?.[1];
        return await mockDataService.notifications.delete(id);
      }
      return await mockDataService.notifications.getAll(params);
    }

    logger.info('Testing Mode: No mock handler found for endpoint:', endpoint);
    return { message: 'Mock endpoint not implemented', endpoint };
  } catch (error) {
    logger.error('Testing Mode: Mock service error:', error);
    throw error;
  }
};

/**
 * Enhanced authenticated request with proactive token checking and improved retry logic
 * P1: Uses cookie-based authentication instead of Authorization headers
 */
const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  const authService = getAuthService?.();
  if (!authService) {
    // If no auth service available, make regular request
    return baseFetch(endpoint, options);
  }

  // PROACTIVE REFRESH: Check if token expires soon (< 5 minutes)
  if (shouldRefreshToken() && !isRefreshingToken) {
    logger.info('Token expiring soon, proactively refreshing');
    
    isRefreshingToken = true;
    try {
      await authService.refreshAccessToken();
      logger.info('Proactive token refresh successful');
    } catch (error) {
      logger.error('Proactive refresh failed:', error);
      // Continue with request anyway, will handle 401 if it fails
    } finally {
      isRefreshingToken = false;
    }
  }

  // P1: Cookies are sent automatically, no need to add Authorization header
  // Just make the request and handle 401 errors with refresh
  try {
    return await baseFetch(endpoint, options);
  } catch (error) {
    // Handle 401 errors with automatic retry (reactive refresh as fallback)
    if (error.status === 401) {
      logger.info('Received 401 error, attempting token refresh for:', endpoint);

      // If we're already refreshing, queue this request
      if (isRefreshingToken) {
        logger.info('Token refresh in progress, queuing request');
        return queueRequest(() => baseFetch(endpoint, options));
      }

      // Try to refresh token
      isRefreshingToken = true;
      try {
        const refreshed = await authService.refreshAccessToken();
        if (refreshed) {
          logger.info('Token refresh successful, retrying request');
          // Process any queued requests
          processQueue(null, true);
          // Retry the original request (new cookies set by refresh)
          return await baseFetch(endpoint, options);
        } else {
          // Refresh failed, process queue with error
          const refreshError = new ApiError('Token refresh failed', 401);
          processQueue(refreshError);
          throw refreshError;
        }
      } catch (refreshError) {
        logger.error('Token refresh failed:', refreshError);
        // Process queue with error
        processQueue(refreshError);
        throw refreshError;
      } finally {
        isRefreshingToken = false;
        refreshPromise = null;
      }
    }

    // Re-throw non-401 errors
    throw error;
  }
};

/**
 * API Client with common HTTP methods
 */
export const apiClient = {
  /**
   * GET request
   */
  get: async (endpoint, params = {}, options = {}) => {
    // Check if we should use mock data
    const mockResponse = await routeToMockIfTesting(endpoint, 'GET', null, params);
    if (mockResponse !== null) {
      return mockResponse;
    }

    const searchParams = new URLSearchParams(params);
    const queryString = searchParams.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    // Use authenticated request for endpoints that need authentication
    // Admin endpoints and user-specific endpoints require auth
    const needsAuth = endpoint.includes('/api/v1/admin/') ||
      endpoint.includes('/api/v1/user/') ||
      endpoint.includes('/api/v1/transactions/me/') ||
      endpoint.includes('/api/v1/topup/') ||
      options.requireAuth === true;
    const fetchFunction = needsAuth ? makeAuthenticatedRequest : baseFetch;

    const response = await fetchFunction(url, {
      method: 'GET',
      ...options,
    });

    return response.json();
  },

  /**
   * POST request
   */
  post: async (endpoint, data = {}, options = {}) => {
    // Check if we should use mock data
    const mockResponse = await routeToMockIfTesting(endpoint, 'POST', data);
    if (mockResponse !== null) {
      return mockResponse;
    }

    // Use authenticated request for endpoints that need authentication
    // Admin endpoints (except auth endpoints), user-specific endpoints, and topup endpoints require auth
    const needsAuth = (endpoint.includes('/api/v1/admin/') && !endpoint.includes('/auth/')) ||
      endpoint.includes('/api/v1/user/') ||
      endpoint.includes('/api/v1/transactions/me/') ||
      endpoint.includes('/api/v1/topup/') ||
      options.requireAuth === true;
    const fetchFunction = needsAuth ? makeAuthenticatedRequest : baseFetch;

    // Check if data is FormData - if so, don't stringify and don't set Content-Type (browser will set it with boundary)
    const isFormData = data instanceof FormData;
    const requestOptions = {
      method: 'POST',
      ...options,
    };

    if (isFormData) {
      // For FormData, let the browser set the Content-Type with the boundary
      requestOptions.body = data;
      // Remove Content-Type if it was set in options
      if (requestOptions.headers) {
        delete requestOptions.headers['Content-Type'];
      }
    } else {
      // For regular JSON data
      requestOptions.headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };
      requestOptions.body = JSON.stringify(data);
    }

    const response = await fetchFunction(endpoint, requestOptions);

    return response.json();
  },

  /**
   * PUT request
   */
  put: async (endpoint, data = {}, options = {}) => {
    // Check if we should use mock data
    const mockResponse = await routeToMockIfTesting(endpoint, 'PUT', data);
    if (mockResponse !== null) {
      return mockResponse;
    }

    // Use authenticated request for endpoints that need authentication
    const needsAuth = endpoint.includes('/api/v1/admin/') ||
      endpoint.includes('/api/v1/user/') ||
      endpoint.includes('/api/v1/transactions/me/') ||
      endpoint.includes('/api/v1/topup/') ||
      options.requireAuth === true;
    const fetchFunction = needsAuth ? makeAuthenticatedRequest : baseFetch;

    // Check if data is FormData - if so, don't stringify and don't set Content-Type
    const isFormData = data instanceof FormData;
    const requestOptions = {
      method: 'PUT',
      ...options,
    };

    if (isFormData) {
      // For FormData, let the browser set the Content-Type with the boundary
      requestOptions.body = data;
      // Remove Content-Type if it was set in options
      if (requestOptions.headers) {
        delete requestOptions.headers['Content-Type'];
      }
    } else {
      // For regular JSON data
      requestOptions.headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };
      requestOptions.body = JSON.stringify(data);
    }

    const response = await fetchFunction(endpoint, requestOptions);

    return response.json();
  },

  /**
   * PATCH request
   */
  patch: async (endpoint, data = {}, options = {}) => {
    // Check if we should use mock data
    const mockResponse = await routeToMockIfTesting(endpoint, 'PATCH', data);
    if (mockResponse !== null) {
      return mockResponse;
    }

    // Use authenticated request for endpoints that need authentication
    const needsAuth = endpoint.includes('/api/v1/admin/') ||
      endpoint.includes('/api/v1/user/') ||
      endpoint.includes('/api/v1/transactions/me/') ||
      endpoint.includes('/api/v1/topup/') ||
      options.requireAuth === true;
    const fetchFunction = needsAuth ? makeAuthenticatedRequest : baseFetch;

    const response = await fetchFunction(endpoint, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: JSON.stringify(data),
      ...options,
    });

    return response.json();
  },

  /**
   * DELETE request
   */
  delete: async (endpoint, options = {}) => {
    // Check if we should use mock data
    const mockResponse = await routeToMockIfTesting(endpoint, 'DELETE');
    if (mockResponse !== null) {
      return mockResponse;
    }

    // Use authenticated request for endpoints that need authentication
    const needsAuth = endpoint.includes('/api/v1/admin/') ||
      endpoint.includes('/api/v1/user/') ||
      endpoint.includes('/api/v1/transactions/me/') ||
      endpoint.includes('/api/v1/topup/') ||
      options.requireAuth === true;
    const fetchFunction = needsAuth ? makeAuthenticatedRequest : baseFetch;

    const response = await fetchFunction(endpoint, {
      method: 'DELETE',
      ...options,
    });

    // DELETE might not return JSON content
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return { success: true };
  },
};

/**
 * Utility function to handle API responses with loading states
 */
export const withApiCall = async (apiCall, setLoading = null, setError = null) => {
  try {
    if (setLoading) setLoading(true);
    if (setError) setError(null);

    const result = await apiCall();
    return result;
  } catch (error) {
    // Only log non-auth errors as actual failures
    if (!isAuthError(error.status)) {
      logger.error('API call failed:', error);
    }
    if (setError) setError(error.message || 'An error occurred');
    throw error;
  } finally {
    if (setLoading) setLoading(false);
  }
};

export default apiClient; 